import { FC, useState } from "react";
import { LineStylePopup } from "shared/ui";
import type { RSISettings, RSILevelStyle, IndicatorLineStyle } from "../../model";
import tabStyles from "./ADXStylesTab.module.css";
import inputStyles from "./AcceleratorOscillatorInputsTab.module.css";

type LevelKey = "level1" | "level2" | "level3";
type PopupKey = "mainLine" | LevelKey;

const LEVELS: LevelKey[] = ["level1", "level2", "level3"];

interface Props {
  settings: RSISettings;
  onChange: (s: RSISettings) => void;
}

export const RSIStylesTab: FC<Props> = ({ settings, onChange }) => {
  const [openPopup, setOpenPopup] = useState<PopupKey | null>(null);
  const [levelValues, setLevelValues] = useState<Record<LevelKey, string>>({
    level1: String(settings.level1.value),
    level2: String(settings.level2.value),
    level3: String(settings.level3.value),
  });

  const toggle = (key: PopupKey) => setOpenPopup((prev) => (prev === key ? null : key));

  const updateMainLine = (patch: Partial<IndicatorLineStyle>) =>
    onChange({ ...settings, mainLine: { ...settings.mainLine, ...patch } });

  const updateLevel = (key: LevelKey, patch: Partial<RSILevelStyle>) =>
    onChange({ ...settings, [key]: { ...settings[key], ...patch } } as RSISettings);

  const handleLevelBlur = (key: LevelKey) => {
    const parsed = parseInt(levelValues[key], 10);
    const clamped = isNaN(parsed) ? settings[key].value : Math.min(Math.max(1, parsed), 100);
    setLevelValues((prev) => ({ ...prev, [key]: String(clamped) }));
    updateLevel(key, { value: clamped });
  };

  return (
    <div className={tabStyles.tab}>
      <div className={tabStyles.row}>
        <div className={tabStyles.left}>
          <label className={tabStyles.checkboxWrap}>
            <input type="checkbox" className={tabStyles.checkboxInput}
              checked={settings.mainLine.visible}
              onChange={(e) => updateMainLine({ visible: e.target.checked })}
            />
            <span className={tabStyles.checkmark} />
          </label>
          <span className={tabStyles.label}>Main Line</span>
        </div>
        <div className={tabStyles.btnWrap}>
          <button className={tabStyles.colorBtn} onMouseDown={(e) => e.stopPropagation()} onClick={() => toggle("mainLine")}>
            <span className={tabStyles.colorBtnSwatch} style={{ background: settings.mainLine.color }} />
            <span className={tabStyles.colorBtnWidth}>{settings.mainLine.width}px</span>
          </button>
          {openPopup === "mainLine" && (
            <LineStylePopup color={settings.mainLine.color} width={settings.mainLine.width}
              onColorChange={(c) => updateMainLine({ color: c })} onWidthChange={(w) => updateMainLine({ width: w })}
              onClose={() => setOpenPopup(null)} />
          )}
        </div>
      </div>
      {LEVELS.map((key) => (
        <div key={key} className={tabStyles.row}>
          <div className={tabStyles.left}>
            <label className={tabStyles.checkboxWrap}>
              <input type="checkbox" className={tabStyles.checkboxInput}
                checked={settings[key].visible}
                onChange={(e) => updateLevel(key, { visible: e.target.checked })}
              />
              <span className={tabStyles.checkmark} />
            </label>
            <input type="number" className={inputStyles.input} min={1} max={100}
              value={levelValues[key]}
              onChange={(e) => setLevelValues((prev) => ({ ...prev, [key]: e.target.value }))}
              onBlur={() => handleLevelBlur(key)}
            />
          </div>
          <div className={tabStyles.btnWrap}>
            <button className={tabStyles.colorBtn} onMouseDown={(e) => e.stopPropagation()} onClick={() => toggle(key)}>
              <span className={tabStyles.colorBtnSwatch} style={{ background: settings[key].color }} />
              <span className={tabStyles.colorBtnWidth}>{settings[key].width}px</span>
            </button>
            {openPopup === key && (
              <LineStylePopup color={settings[key].color} width={settings[key].width}
                onColorChange={(c) => updateLevel(key, { color: c })} onWidthChange={(w) => updateLevel(key, { width: w })}
                onClose={() => setOpenPopup(null)} />
            )}
          </div>
        </div>
      ))}
    </div>
  );
};
